// Typed wrappers for the Worker's JSON API (docs/ARCHITECTURE.md, "HTTP API").
// Same origin only: the HttpOnly session cookie rides along, every POST is JSON, and any failure
// (HTTP error, network error, timeout, malformed reply) is thrown as an ApiError.
import { isSkinId, NAME_MAX, ROOM_CODE_RE, SKINS, type SkinId } from '../shared/config';

// What GET /api/me returns. Server truth: the client only displays it, it never decides it.
export interface Profile {
  name: string; skin: SkinId; points: number; owned: SkinId[]; unlocked: boolean;
  best: number; // best mass ever
  // entitlements: only on GET /api/me and replies that unlock (undefined elsewhere: keep the last ones seen)
  free?: boolean; // FREE_PLAY: every mode playable without the unlock, with ads
  ads?: { client: string; slot: string | null } | null; // AdSense for this (unpaid) profile; null = no ads
  code?: { code: string; used: boolean } | null; // the buyer's one-time code for one other profile
}
export interface SoloResultBody { bestRank: number; peakMass: number; aliveSec: number; kills: number; goldenKills: number }
// what POST /api/solo/result is sent: the life's stats plus the single-use ticket from POST /api/solo/start
export type SoloResultPost = SoloResultBody & { ticket?: string };
export interface SoloAward { profile: Profile; awarded: number } // POST /api/solo/result reply
// POST /api/solo/start reply: an HMAC-signed, single-use ticket for one solo life, and when the Worker issued it
export interface SoloTicket { ticket: string; startedAt: number }
// redeem and checkout/verify: did it unlock, plus the fresh profile when the server sent one
export interface UnlockResult { unlocked: boolean; profile: Profile | null }

export class ApiError extends Error {
  readonly status: number; // HTTP status; 0 = network failure or timeout
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

const TIMEOUT_MS = 12000;
const FREE_SKINS = SKINS.filter(s => !s.price).map(s => s.id);
type Json = Record<string, unknown>;
const isObj = (x: unknown): x is Json => typeof x === 'object' && x !== null && !Array.isArray(x);
const num = (x: unknown, d = 0) => (typeof x === 'number' && Number.isFinite(x) ? x : d);

// Normalises a profile reply ({...profile} or {profile: {...}}); null if it doesn't look like one.
export function toProfile(x: unknown): Profile | null {
  const o = isObj(x) && isObj(x.profile) ? x.profile : x;
  if (!isObj(o) || typeof o.points !== 'number') return null;
  const owned = new Set<SkinId>(FREE_SKINS); // free skins are always yours
  if (Array.isArray(o.owned)) for (const s of o.owned) if (isSkinId(s) && s !== 'gold') owned.add(s);
  const skin = isSkinId(o.skin) && owned.has(o.skin) ? o.skin : 'blue';
  return {
    name: typeof o.name === 'string' ? o.name.slice(0, NAME_MAX) : '',
    skin,
    points: Math.max(0, Math.floor(num(o.points))),
    owned: [...owned],
    unlocked: o.unlocked === true || o.unlocked === 1,
    best: Math.max(0, Math.floor(num(o.best))),
    ...entitlements(o),
  };
}
function entitlements(o: Json): Pick<Profile, 'free' | 'ads' | 'code'> {
  if (typeof o.free !== 'boolean') return {};
  const a = o.ads, c = o.code;
  return {
    free: o.free,
    ads: isObj(a) && typeof a.client === 'string' ? { client: a.client, slot: typeof a.slot === 'string' ? a.slot : null } : null,
    code: isObj(c) && typeof c.code === 'string' && /^CG-[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{4}$/.test(c.code) ? { code: c.code, used: c.used === true } : null,
  };
}
function toUnlock(x: unknown): UnlockResult | null {
  if (!isObj(x)) return null;
  const profile = toProfile(x);
  return { unlocked: x.unlocked === true || !!profile?.unlocked, profile };
}
const TICKET_MAX = 512;
const toTicket = (x: unknown): SoloTicket | null =>
  (isObj(x) && typeof x.ticket === 'string' && x.ticket.length > 0 && x.ticket.length <= TICKET_MAX
    && typeof x.startedAt === 'number' && Number.isFinite(x.startedAt) ? { ticket: x.ticket, startedAt: x.startedAt } : null);
const toRoom = (x: unknown) => (isObj(x) && typeof x.room === 'string' && ROOM_CODE_RE.test(x.room) ? { room: x.room } : null);

// keepalive: the request outlives the page (a result posted just before the tab closes still arrives)
async function request<T>(method: 'GET' | 'POST', path: string, body: unknown, parse: (data: unknown) => T | null, keepalive = false, extra?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...extra };
  const init: RequestInit = { method, headers, credentials: 'same-origin', cache: 'no-store' };
  if (keepalive) init.keepalive = true;
  // the Worker rejects POSTs that aren't JSON, so even empty ones send {}
  if (method === 'POST') { headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(body ?? {}); }
  const ctl = new AbortController(), timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
  init.signal = ctl.signal;
  let res: Response, text: string;
  try {
    res = await fetch(path, init);
    text = await res.text();
  } catch {
    throw new ApiError(0, ctl.signal.aborted ? 'Request timed out' : 'Network error');
  } finally {
    clearTimeout(timer);
  }
  let data: unknown = null;
  if (text) try { data = JSON.parse(text); } catch { data = null; }
  if (!res.ok) {
    const msg = isObj(data) && typeof data.error === 'string' ? data.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }
  const out = parse(data);
  if (out === null) throw new ApiError(res.status, 'Unexpected response from server', data);
  return out;
}

// ========== endpoints ==========
// Framed on an allowed embedding site: ask for the partitioned embed session (worker session.ts EMBED_HEADER).
export const isFramed = (): boolean => { try { return typeof window !== 'undefined' && window.self !== window.top; } catch { return true; } };
export const getMe = () => request('GET', '/api/me', undefined, toProfile, false, isFramed() ? { 'X-CG-Embed': '1' } : undefined);
export const updateMe = (patch: { name?: string; skin?: SkinId }) => request('POST', '/api/me', patch, toProfile);
export const buySkin = (skin: SkinId) => request('POST', '/api/shop/buy', { skin }, toProfile);
// a solo life starts: the Worker signs a ticket the result must carry (unlocked profiles, or anyone under FREE_PLAY)
export const soloStart = () => request('POST', '/api/solo/start', {}, toTicket);
// the solo reply is the profile plus `awarded`: what this life actually earned after the Worker's checks
export const soloResultAward = (r: SoloResultPost) => request('POST', '/api/solo/result', r, (d): SoloAward | null => {
  const profile = toProfile(d);
  return profile && { profile, awarded: isObj(d) ? Math.max(0, Math.floor(num(d.awarded))) : 0 };
}, true);
export const soloResult = (r: SoloResultPost) => soloResultAward(r).then(a => a.profile);
// The page is going away (tab hidden or closed) mid-life: sendBeacon is the one request a closing page
// reliably gets out. Fire and forget, no reply. Falls back to a keepalive fetch; false if nothing went out.
export function soloResultBeacon(r: SoloResultPost): boolean {
  const json = JSON.stringify(r);
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.sendBeacon === 'function' && nav.sendBeacon('/api/solo/result', new Blob([json], { type: 'application/json' }))) return true;
  } catch { /* no beacon here: try fetch */ }
  try {
    fetch('/api/solo/result', {
      method: 'POST', keepalive: true, credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: json,
    }).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}
export const redeem = (code: string) => request('POST', '/api/redeem', { code }, toUnlock);
export const checkout = () =>
  request('POST', '/api/checkout', {}, d => (isObj(d) && typeof d.url === 'string' ? { url: d.url } : null));
// Embedded Checkout: the form runs in a window on this page (Stripe.js), no redirect to stripe.com
export const checkoutEmbedded = () =>
  request('POST', '/api/checkout', { embedded: true }, d => (isObj(d) && typeof d.clientSecret === 'string' && typeof d.sessionId === 'string'
    && typeof d.publishableKey === 'string' && d.publishableKey.startsWith('pk_')
    ? { clientSecret: d.clientSecret, sessionId: d.sessionId, publishableKey: d.publishableKey } : null));
export const verifyCheckout = (sessionId: string) =>
  request('GET', `/api/checkout/verify?session_id=${encodeURIComponent(sessionId)}`, undefined, toUnlock);
export const match = () => request('GET', '/api/match', undefined, toRoom);
export const createRoom = () => request('POST', '/api/room', {}, toRoom);
export const health = () => request('GET', '/api/health', undefined, d => (isObj(d) && d.ok === true ? { ok: true as const } : null));

export const api = {
  me: getMe, updateMe, buySkin, soloStart, soloResult, soloResultAward, soloResultBeacon, redeem, checkout, checkoutEmbedded, verifyCheckout, match, createRoom, health,
};
